import { useRef, useState } from "react";
import { UploadCloud, FileSpreadsheet, X } from "lucide-react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../../store";
import { outwardProductsExcel } from "../../../store/slices/Factory/newFloorSlice";

interface OutwardExcelUploadModalProps {
  batchNo: string | number;
  onClose: (success: boolean) => void;
}

export default function OutwardExcelUploadModal({
  batchNo,
  onClose,
}: OutwardExcelUploadModalProps) {
  const dispatch: RootDispatch = useDispatch();
  const loading = useSelector(
    (state: RootState) => state.newFloor.loading.outwardProductsExcelLoading,
  );
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const acceptExtensions = [".xls", ".xlsx", ".csv"];

  const isValidExcelFile = (candidate: File) => {
    const name = candidate.name.toLowerCase();
    return acceptExtensions.some((ext) => name.endsWith(ext));
  };

  const handleFile = (candidate: File) => {
    if (!isValidExcelFile(candidate)) {
      toast.error("Please upload a valid Excel file (.xls, .xlsx, .csv)");
      return;
    }
    setFile(candidate);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) handleFile(dropped);
  };

  const handleSubmit = async () => {
    if (!file) {
      toast.error("Please select a file to upload");
      return;
    }
    try {
      await dispatch(outwardProductsExcel({ batchNo, file })).unwrap();
      toast.success("Excel uploaded successfully");
      onClose(true);
    } catch (error: any) {
      toast.error(error || "Failed to upload excel");
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-[110]">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-slate-100">
        <div className="bg-slate-50 px-6 py-5 flex justify-between items-center border-b border-slate-200/80">
          <div>
            <h2 className="text-lg font-extrabold text-slate-900 tracking-tight">
              Upload Outward Excel
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              Batch #{batchNo} - upload serial numbers via Excel
            </p>
          </div>
          <button
            onClick={() => onClose(false)}
            className="p-1.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-200/60 transition-colors focus:outline-none"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 bg-white">
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`cursor-pointer flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-8 text-center transition-colors ${
              isDragging
                ? "border-blue-500 bg-blue-50"
                : "border-slate-300 hover:border-slate-400 bg-slate-50"
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xls,.xlsx,.csv"
              className="hidden"
              onChange={(e) => {
                const selected = e.target.files?.[0];
                if (selected) handleFile(selected);
              }}
            />
            {file ? (
              <>
                <FileSpreadsheet className="w-8 h-8 text-green-600" />
                <p className="text-sm font-medium text-slate-700">
                  {file.name}
                </p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                  }}
                  className="text-xs text-red-500 hover:underline"
                >
                  Remove
                </button>
              </>
            ) : (
              <>
                <UploadCloud className="w-8 h-8 text-slate-400" />
                <p className="text-sm font-medium text-slate-600">
                  Drag & drop excel file here, or click to browse
                </p>
                <p className="text-xs text-slate-400">
                  Supported formats: .xls, .xlsx, .csv
                </p>
              </>
            )}
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={() => onClose(false)}
              disabled={loading}
              className="px-5 py-2 font-semibold rounded-md border border-gray-300 text-gray-700 bg-gray-100 hover:bg-gray-200 transition-all disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || !file}
              className="px-5 py-2 font-semibold rounded-md bg-blue-600 hover:bg-blue-700 text-white transition-all disabled:opacity-50"
            >
              {loading ? "Uploading..." : "Upload"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
